<template>
  <div class="swiper-slide lineup__item">
    <AtomsConsumerLink class="lineup__btn" :url="searchLink">
      <div class="lineup__head">
        <h2>{{ product.name }}</h2>
        <p>商品展開：{{ productDevelopmentName }}</p>
        <figure :class="`slide__img__${product.images.length}`">
          <AtomsConsumerImage
            v-for="image in product.images"
            :key="image"
            :src="image"
            :width="992"
            :height="1323"
            :alt="product.name"
          />
        </figure>
      </div>
      <div class="lineup__body">
        <div class="column2">
          {{ productDevelopmentPrice }}
          <small>(参考税込)</small>
        </div>
        <div class="column1">{{ productSex }}</div>
        <div class="column1">{{ productFabric }}</div>
        <div class="column1">{{ productSize }}</div>
        <div class="color__tip__container column2">
          <div v-if="isOneTargetColors" class="color__tip__wrap">
            <FeaturesHowtochooseRecoverywearSleepitemPartsColorTip :colors="product.sex[0].colors" />
          </div>
          <template v-else>
            <div class="color__tip__wrap">
              <span class="text">メンズ</span>
              <FeaturesHowtochooseRecoverywearSleepitemPartsColorTip
                :colors="product.sex.find((sex) => sex.name === 'mens')?.colors ?? []"
              />
            </div>
            <div class="color__tip__wrap">
              <span class="text">ウィメンズ</span>
              <FeaturesHowtochooseRecoverywearSleepitemPartsColorTip
                :colors="product.sex.find((sex) => sex.name === 'ladies')?.colors ?? []"
              />
            </div>
          </template>
        </div>
        <div class="column1">{{ productSeason }}</div>
      </div>
    </AtomsConsumerLink>
  </div>
</template>

<script setup lang="ts">
import {
  RecommendLineupProduct,
  getFabric,
  getSize,
  getSex,
  getSeason,
} from "~/utils/functions/howtochoose_recoverywear_sleepitem"

const props = defineProps<{
  product: RecommendLineupProduct
}>()

const names = {
  "short-sleeve-short-pants": "半袖",
  "long-sleeve-long-pants": "長袖",
  "one-piece": "ワンピース",
}

/**
 * 商品展開（半袖、長袖、ワンピース）の文言を返す
 */
const productDevelopmentName = computed(() => {
  const productDevelopmentNames = props.product.productDevelopments.map(
    (productDevelopment) => names[productDevelopment.name] || "",
  )
  return productDevelopmentNames.join(" / ")
})

/**
 * 商品展開（半袖、長袖、ワンピース）ごとの価格を返す
 */
const productDevelopmentPrice = computed(() => {
  if (props.product.productDevelopments.length === 0) {
    return ""
  } else if (props.product.productDevelopments.length > 1) {
    const productDevelopmentPrices = props.product.productDevelopments.map((productDevelopment) => {
      return (names[productDevelopment.name] || "") + " ¥" + productDevelopment.price.toLocaleString()
    })
    return productDevelopmentPrices.join(" / ")
  } else {
    return "¥" + props.product.productDevelopments[0].price.toLocaleString()
  }
})

/**
 * 商品の性別（メンズ、ウィメンズ）の文言を返す
 */
const productSex = computed(() => {
  const productSexes = props.product.sex.map((sex) => {
    return getSex(sex.name)
  })
  return productSexes.join(" / ")
})

/**
 * 商品の素材の文言を返す
 */
const productFabric = computed(() => {
  return getFabric(props.product.fabric)
})

/**
 * 商品のサイズの文言を返す
 */
const productSize = computed(() => {
  const productSizes = props.product.sex.map((sex) => {
    return getSex(sex.name) + ":" + getSize(sex.size)
  })
  return productSizes.join(" / ")
})

/**
 * 商品展開（半袖、長袖、ワンピース）ごとの季節の文言を返す
 */
const productSeason = computed(() => {
  if (props.product.productDevelopments.length === 0) {
    return ""
  } else if (props.product.productDevelopments.length === 1) {
    // 商品展開が一つの場合
    return getSeason(props.product.productDevelopments[0].season)
  } else {
    const season = props.product.productDevelopments[0].season
    if (props.product.productDevelopments.every((productDevelopment) => productDevelopment.season === season)) {
      // 商品展開は複数だが、季節は全て同じ場合
      return getSeason(season)
    } else {
      // 商品展開ごとに季節を表示
      const productDevelopmentPrices = props.product.productDevelopments.map((productDevelopment) => {
        return (names[productDevelopment.name] || "") + ":" + getSeason(productDevelopment.season)
      })
      return productDevelopmentPrices.join(" / ")
    }
  }
})

/**
 * 性別が一つ、もしくは性別は複数あるが、カラーが全く同じかどうかを判定する
 */
const isOneTargetColors = computed(() => {
  if (props.product.sex.length === 1) {
    return true
  }
  // メンズとウィメンズのcolorsが全く同じかどうかを判定
  const mensColors = props.product.sex.find((sex) => sex.name === "mens")?.colors
  const ladiesColors = props.product.sex.find((sex) => sex.name === "ladies")?.colors
  if (mensColors && ladiesColors) {
    return (
      mensColors.every((color) => ladiesColors.some((c) => c.name === color.name)) &&
      ladiesColors.every((color) => mensColors.some((c) => c.name === color.name))
    )
  }
  return false
})

const searchLink = computed(() => {
  const name = props.product.name ?? ""
  const fabric = props.product.fabric ?? ""
  const silhouette = props.product.silhouette ?? ""
  return `/products?keywords=${encodeURIComponent(name)}&fabric=${encodeURIComponent(
    fabric,
  )}&silhouette=${encodeURIComponent(silhouette)}&category=63181bf0e7289307df9017e2`
})
</script>

<style scoped lang="scss">
@import "assets/scss/pages/howtochoose_recoverywear_sleepitem/main";
</style>
